import { Phone, Mail } from "lucide-react";
import SEO, { orgJsonLd, localBusinessJsonLd } from "@/components/SEO";
import { CONTACT } from "@/components/NSLogo";
import { RevealBlock } from "@/components/Reveal";

export default function Contact() {
  return (
    <>
      <SEO
        title="Contact Nova Scotia Security Services"
        description={`Contact Maze Security Solutions — Nova Scotia Security Services. Phone ${CONTACT.phone}. Email ${CONTACT.email}. Licensed security guard services across Halifax and every Nova Scotia community.`}
        path="/contact"
        keywords={[
          "contact Nova Scotia Security Services",
          "Maze Security Solutions Halifax",
          "security company phone Nova Scotia",
          "security company email Halifax",
          "security guard quote Nova Scotia",
        ]}
        jsonLd={[
          orgJsonLd,
          {
            ...localBusinessJsonLd,
            telephone: CONTACT.phone,
            email: CONTACT.email,
          },
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            name: "Contact Nova Scotia Security Services",
            url: "https://novascotiaguards.ca/contact",
            mainEntity: {
              "@type": "Organization",
              name: CONTACT.parentBrand,
              telephone: CONTACT.phone,
              email: CONTACT.email,
            },
          },
        ]}
      />
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-16 md:pt-24 pb-24">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#C17A43] mb-8">
          — Contact · {CONTACT.parentBrand}
        </p>
        <RevealBlock>
          <h1 className="font-serif text-[clamp(3rem,9vw,9rem)] leading-[0.88] tracking-tighter text-[#111111]">
            Reach a<br />
            <span className="italic text-[#184C59]">licensed supervisor.</span>
          </h1>
        </RevealBlock>
        <p className="mt-8 font-sans text-lg md:text-xl text-[#111111]/70 max-w-[64ch] leading-relaxed">
          A supervisor answers every call and every message. For same-day
          dispatch across Halifax, Dartmouth, Sydney, Truro, and the wider
          province, phone the office directly. For quote briefs and written
          documentation, email us.
        </p>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#111111] border border-[#111111]">
          <a
            href={CONTACT.phoneHref}
            data-testid="contact-page-phone"
            className="group bg-[#F5F3EF] p-10 md:p-14 hover:bg-[#111111] hover:text-[#F5F3EF] transition-colors duration-500 min-h-[280px] flex flex-col justify-between"
          >
            <div className="flex items-center gap-3">
              <Phone size={20} strokeWidth={1.5} />
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C17A43]">
                — Phone
              </p>
            </div>
            <div className="mt-10">
              <p className="font-serif text-4xl md:text-6xl leading-none tracking-tight group-hover:italic transition-[font-style] duration-300">
                {CONTACT.phone}
              </p>
              <p className="mt-6 font-sans text-sm opacity-70">
                Answered 24 / 7 / 365 by a licensed supervisor.
              </p>
            </div>
          </a>

          <a
            href={CONTACT.emailHref}
            data-testid="contact-page-email"
            className="group bg-[#F5F3EF] p-10 md:p-14 hover:bg-[#111111] hover:text-[#F5F3EF] transition-colors duration-500 min-h-[280px] flex flex-col justify-between"
          >
            <div className="flex items-center gap-3">
              <Mail size={20} strokeWidth={1.5} />
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C17A43]">
                — Email
              </p>
            </div>
            <div className="mt-10">
              <p className="font-serif text-3xl md:text-5xl leading-tight tracking-tight break-all group-hover:italic transition-[font-style] duration-300">
                {CONTACT.email}
              </p>
              <p className="mt-6 font-sans text-sm opacity-70">
                Replies within one business day, in writing.
              </p>
            </div>
          </a>
        </div>

        <div className="mt-16 grid grid-cols-12 gap-8 border-t border-[#D9D6D0] pt-16">
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C17A43]">
              — Halifax Office
            </p>
            <p className="mt-4 font-serif text-xl leading-tight text-[#111111]">
              {CONTACT.parentBrand}
              <br />
              Nova Scotia Security Services
              <br />
              Barrington Street
              <br />
              Halifax, Nova Scotia
              <br />
              B3J · Canada
            </p>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C17A43]">
              — Hours
            </p>
            <p className="mt-4 font-serif text-xl leading-tight text-[#111111]">
              Dispatch — 24 / 7 / 365
              <br />
              Office — Mon–Fri, 08:00–18:00 AST
              <br />
              Written briefs — always open
            </p>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#C17A43]">
              — Coverage
            </p>
            <p className="mt-4 font-serif text-xl leading-tight text-[#111111]">
              Halifax · Dartmouth · Sydney
              <br />
              Truro · New Glasgow · Yarmouth
              <br />
              Bridgewater · Cape Breton
              <br />
              65 Nova Scotia communities
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
